import type { PropsWithChildren } from 'react';
import { memo, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { isMobile } from '@frameio/vapor';
import { useIsFlattenFoldersEnabled } from '../../../shared';
import { getReorderDroppableId } from '../utils/op-checks';
import { addZoneToId } from './DndOpsContext';
import { useAssetDndZone } from './DndZoneContext';

type ContextValue = {
  draggableProps: ReturnType<typeof useDraggable> | undefined;
  droppableProps: ReturnType<typeof useDroppable> | undefined;
};
type AssetDndContextProviderProps = {
  id: string;
};

export const AssetDndContextProvider = memo(
  ({ children, id }: PropsWithChildren<AssetDndContextProviderProps>) => {
    const flattenFolders = useIsFlattenFoldersEnabled();
    /**
     * Asset Dnd behavior is disabled when:
     * - flattenFolders appearance settings is enabled
     * - User is on mobile
     */
    const disabled = flattenFolders || isMobile;

    const zone = useAssetDndZone();
    const idWithZone = addZoneToId(id, zone);
    const draggableProps = useDraggable({
      id: idWithZone,
      disabled,
      data: { zone },
    });
    const droppableProps = useDroppable({
      id: idWithZone,
      disabled,
      data: { zone },
    });

    const value: ContextValue = useMemo(
      () => ({
        draggableProps,
        droppableProps,
      }),
      [draggableProps, droppableProps]
    );

    return (
      <AssetDndContext.Provider value={value}>
        {children}
      </AssetDndContext.Provider>
    );
  }
);

const AssetDndContext = createContext<ContextValue>({
  draggableProps: undefined,
  droppableProps: undefined,
});

export const useDraggableAssetActivator = () => {
  const setActivatorNodeRef = useContextSelector(
    AssetDndContext,
    ({ draggableProps }) => draggableProps?.setActivatorNodeRef
  );
  const listeners = useContextSelector(
    AssetDndContext,
    ({ draggableProps }) => draggableProps?.listeners
  );
  const setNodeRef = useContextSelector(
    AssetDndContext,
    ({ draggableProps }) => draggableProps?.setNodeRef
  );

  return {
    setActivatorNodeRef,
    listeners,
    setNodeRef,
  };
};

export const useSetDroppableNodeRef = () =>
  useContextSelector(
    AssetDndContext,
    ({ droppableProps }) => droppableProps?.setNodeRef
  );

export const useReorderDroppable = ({
  beforeId,
  afterId,
}: {
  beforeId: string | null | undefined;
  afterId: string | null | undefined;
}) => {
  return useDroppable({
    id: getReorderDroppableId(beforeId, afterId),
    data: {},
  });
};
